import { CartCoreModule } from '@wix/cart-core';
import {
  CycleType,
  type DurationUnit,
} from '@wix/domains-serverless-interface';
import { DomainsApiClient } from '@wix/domains-serverless-interface/react-kit';
import { useState } from 'react';

const DEFAULT_CYCLE = {
  cycleType: CycleType.RECURRING,
  cycleDuration: { unit: 'YEAR' as DurationUnit, count: 3 },
};

const FlowTypes = {
  STOREFRONT: 'storefront',
} as const;

export const useAddDomainToCart = () => {
  const { query: getDomainLineItemWithAddonQuery } =
    DomainsApiClient.fetchHooks.useGetDomainLineItemWithAddon({});
  const { query: getDomainAddonProductsQuery } =
    DomainsApiClient.fetchHooks.useGetDomainAddonProducts({});
  const [loadingDomainName, setLoadingDomainName] = useState<string | null>(
    null,
  );

  const addDomainToCart = async ({ domainName }: { domainName: string }) => {
    setLoadingDomainName(domainName);
    try {
      const [lineItemResult, addonEligibilityResult] = await Promise.all([
        getDomainLineItemWithAddonQuery({
          cycle: DEFAULT_CYCLE,
          domain: domainName,
          core: true,
          customOfferingFilters: { flow: FlowTypes.STOREFRONT },
        }),
        getDomainAddonProductsQuery({ domain: domainName }).catch(() => null),
      ]);

      const { domainLineItem, domainAddonLineItem } = lineItemResult.data ?? {};

      const recommendedProductId = domainAddonLineItem?.productInfo?.productId;
      const eligibleProductIds = new Set(
        addonEligibilityResult?.data?.addons?.map((addon) => addon.productId) ??
          [],
      );
      const isAddonEligible =
        !!recommendedProductId && eligibleProductIds.has(recommendedProductId);

      const lineItems = [
        domainLineItem,
        isAddonEligible ? domainAddonLineItem : undefined,
      ].filter((item): item is NonNullable<typeof item> => Boolean(item));

      await CartCoreModule.SDK.CartUpsertQueue.setPendingUpsert(lineItems);
    } finally {
      setLoadingDomainName(null);
    }
  };

  const isLoading = loadingDomainName !== null;

  return {
    addDomainToCart,
    isLoading,
    loadingDomainName,
  };
};
